import {
  DOC_PERMISSION_RANK,
  type DocFolder,
  type DocGrant,
  type DocPermissionLevel,
} from "@estelle/domain";

/** `folderId` and every ancestor above it, nearest first, root last. */
export function ancestorChain(
  folderId: string,
  folders: readonly DocFolder[],
): DocFolder[] {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const chain: DocFolder[] = [];
  let current = byId.get(folderId);
  while (current) {
    chain.push(current);
    current = current.parentId ? byId.get(current.parentId) : undefined;
  }
  return chain;
}

function principalKey(
  grant: Pick<DocGrant, "principalType" | "principalId">,
): string {
  return `${grant.principalType}:${grant.principalId}`;
}

/** One principal's resolved access to a folder, and where it came from. */
export interface EffectiveGrantRow {
  principalType: DocGrant["principalType"];
  principalId: string;
  /** The grant that actually determines this principal's access here. */
  grant: DocGrant;
  /** An ancestor's grant this one supersedes, when there is one. */
  overrides?: DocGrant;
}

/**
 * Who holds what on `folderId`, once inheritance and override are resolved.
 *
 * Mirrors the fixture adapter's own `effectiveLevel` — nearest ancestor with
 * a grant wins (FR-DOC-8) — but keeps every principal separately rather
 * than collapsing to one rank for "the current user". A grants screen has
 * to answer "who can read this" for everyone, not just the viewer; folding
 * that into a single number is what a permissions screen showing only its
 * own folder's explicit grants gets wrong.
 */
export function resolveFolderAccess(
  folderId: string,
  folders: readonly DocFolder[],
  grantsByFolder: ReadonlyMap<string, readonly DocGrant[]>,
): EffectiveGrantRow[] {
  const chain = ancestorChain(folderId, folders);
  const seen = new Set<string>();
  const rows: EffectiveGrantRow[] = [];

  chain.forEach((folder, depth) => {
    const grants = grantsByFolder.get(folder.id) ?? [];
    for (const grant of grants) {
      const key = principalKey(grant);
      if (seen.has(key)) continue;
      seen.add(key);

      // What this grant supersedes, if the same principal also holds a
      // grant further up the chain — the case that makes the descendant
      // `none` on /finance/payroll legible as a revocation rather than a
      // mystery.
      let overrides: DocGrant | undefined;
      for (const ancestor of chain.slice(depth + 1)) {
        const match = (grantsByFolder.get(ancestor.id) ?? []).find(
          (candidate) => principalKey(candidate) === key,
        );
        if (match) {
          overrides = match;
          break;
        }
      }

      rows.push({
        principalType: grant.principalType,
        principalId: grant.principalId,
        grant,
        overrides,
      });
    }
  });

  return rows.sort(
    (a, b) =>
      DOC_PERMISSION_RANK[b.grant.level] - DOC_PERMISSION_RANK[a.grant.level],
  );
}

export const LEVEL_LABEL: Record<DocPermissionLevel, string> = {
  none: "None",
  read: "Read",
  comment: "Comment",
  write: "Write",
  manage: "Manage",
};

/** Weakest to strongest — the order the level `<select>`s offer them in. */
export const LEVEL_OPTIONS: DocPermissionLevel[] = [
  "none",
  "read",
  "comment",
  "write",
  "manage",
];

/**
 * Narrows a `<select>`'s raw string value back to `DocPermissionLevel`
 * without a cast — Radix's `onValueChange` hands back a bare `string`, and
 * every value it can possibly carry came from `LEVEL_OPTIONS` in the first
 * place.
 */
export function isPermissionLevel(value: string): value is DocPermissionLevel {
  return LEVEL_OPTIONS.some((level) => level === value);
}
